import { T, useGT } from 'gt-react';
import { PricelistReceivedAs } from '@erp/shared-types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
// A real <button>: Radix's `asChild` does not make a bare SVG focusable, so a
// raw icon trigger is unreachable by keyboard.
import { TooltipInfoIcon } from '@/components/common/TooltipInfoIcon';

/**
 * The field controls the pricelist DETAILS shape is edited through, shared by
 * every surface that edits it.
 *
 * Extracted from `PricelistDetailsCard` when the "create a case from selected
 * files" dialog (GEN-2524) needed the same eight fields: the dialog shows the
 * values it seeded from the selected files' legacy details rows and lets them
 * be corrected before saving, which is the same set of inputs the card edits.
 * Two copies would have drifted on the first label change — and the labels are
 * load-bearing here ("AS/400 upload date" exists only to tell two upload dates
 * apart).
 */

/** One labelled read-only field. */
export function Field({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{children}</span>
    </div>
  );
}

/** A section heading inside the card. */
export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
      {children}
    </span>
  );
}

/** A calendar-date field. Native, so it gets the platform's date picker. */
export function DateField({
  id,
  label,
  value,
  disabled,
  onChange,
}: {
  id: string;
  label: React.ReactNode;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Input
        id={id}
        type="date"
        value={value}
        disabled={disabled}
        className="h-8 w-40 text-sm"
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

/** Sentinel for the select's "no value" item — Radix items can't be ''. */
export const RECEIVED_AS_UNSET = 'unset';

/**
 * A select value as a recorded option, '' for the sentinel, or null when it is
 * neither. Radix hands back a plain string, so the sentinel and the enum are
 * the only values the form should ever store.
 */
export function toReceivedAs(value: string): PricelistReceivedAs | '' | null {
  if (value === RECEIVED_AS_UNSET) return '';
  return (
    Object.values(PricelistReceivedAs).find((option) => option === value) ??
    null
  );
}

/** What each received-as value reads as on screen. */
export function receivedAsLabel(
  gt: ReturnType<typeof useGT>,
  receivedAs: PricelistReceivedAs,
): string {
  switch (receivedAs) {
    case PricelistReceivedAs.Notice:
      return gt('Notice', {
        context: 'pricelist received-as option: an advance notice document',
      });
    case PricelistReceivedAs.File:
      return gt('File', {
        context: 'pricelist received-as option: the price file itself',
      });
    case PricelistReceivedAs.NoticeAndFile:
      return gt('Notice + file', {
        context: 'pricelist received-as option: both a notice and the file',
      });
  }
}

/**
 * What the vendor sent, as a select. The label carries the explainer: the
 * distinction feeds the price-file log's NOTES column.
 */
export function ReceivedAsField({
  value,
  disabled,
  onChange,
}: {
  value: PricelistReceivedAs | '';
  disabled: boolean;
  onChange: (value: PricelistReceivedAs | '') => void;
}) {
  const gt = useGT();
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <Label
          htmlFor="pricelist-received-as"
          className="text-xs text-muted-foreground"
        >
          <T context="label for what the vendor sent for a price change">
            Received as
          </T>
        </Label>
        <TooltipInfoIcon
          description={gt(
            'What the vendor sent for this price change: an advance notice, the price file itself, or both. Feeds the NOTES column of the price file log export.',
            { context: 'tooltip explaining the pricelist received-as field' },
          )}
          ariaLabel={gt('About the received-as field', {
            context: 'accessible name for the received-as help icon',
          })}
        />
      </div>
      <Select
        value={value === '' ? RECEIVED_AS_UNSET : value}
        disabled={disabled}
        onValueChange={(next) => {
          const chosen = toReceivedAs(next);
          if (chosen !== null) onChange(chosen);
        }}
      >
        <SelectTrigger id="pricelist-received-as" className="h-8 w-36 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={RECEIVED_AS_UNSET}>
            <T context="pricelist received-as option: nothing recorded">
              Not set
            </T>
          </SelectItem>
          {Object.values(PricelistReceivedAs).map((option) => (
            <SelectItem key={option} value={option}>
              {receivedAsLabel(gt, option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
